import { useQueries } from '@tanstack/react-query'
import useApi from '../../../../hooks/useApi'
import { rangeFilter } from './period'

/*
 * Records, not aggregates: the newest rows of one or more lists within the
 * widget's period — what the record table and the activity feed show.
 *
 *     { entity, filter: 'state:final', dateField: 'date', orderBy, direction, limit }
 *
 * The period becomes the list's own `from`/`to` filter on `dateField`, applied
 * by the server before it pages, so `limit` is the newest N of the period and
 * not N rows picked from a page that happened to be loaded.
 */
const fetchList = async (getMe, source, range) => {
    const dateField = source.dateField ?? 'date'
    const params = new URLSearchParams({
        filter: [source.filter, rangeFilter(range, dateField)].filter(Boolean).join(';'),
        orderBy: source.orderBy ?? dateField,
        direction: source.direction ?? 'desc',
        limit: String(source.limit ?? 10),
    })
    const response = await getMe(`${source.entity}/?${params.toString()}`)
    if (Array.isArray(response?.data)) return response.data
    // An empty collection answers {} with no data key
    if (response && !response.status && !response.error) return []
    throw new Error(response?.error ?? `Could not read ${source.entity}`)
}

const useEntityRows = (sources = [], range) => {
    const { getMe } = useApi()
    const results = useQueries({
        queries: sources.map(source => ({
            queryKey: ['analyticsRows', source.entity, source.filter ?? '', source.dateField ?? 'date', source.orderBy ?? '', source.direction ?? '', source.limit ?? 10, range.from.toISOString(), range.to.toISOString()],
            queryFn: () => fetchList(getMe, source, range),
            placeholderData: (prev) => prev,
            staleTime: 30_000,
        })),
    })
    return {
        isLoading: results.some(r => r.isLoading),
        error: results.find(r => r.isError)?.error ?? null,
        refetch: () => results.forEach(r => r.refetch()),
        rows: results.map(r => r.data ?? []),
    }
}

export default useEntityRows
